/**
 * Argus Live — typed postMessage protocol for parent ↔ iframe communication.
 *
 * PR AL-A3 (Wave A foundation). Implements ARGUS-LIVE-SPEC §4.3.
 *
 * ─── Threat model context ───────────────────────────────────────────────
 *
 * The iframe runs in a **null-origin** sandbox (CSP `sandbox allow-scripts`
 * with NO `allow-same-origin` — see csp-builder.ts). Three consequences
 * for postMessage:
 *
 * 1. The iframe's `window.origin` is the string `"null"`. Origin-based
 *    sender verification (`event.origin === expected`) therefore CANNOT
 *    be used as the primary trust signal. The host MUST instead compare
 *    `event.source === iframeRef.contentWindow` — direct identity check
 *    on the Window object the host itself mounted. This is documented
 *    on the host listener (AL-B1) and asserted in tests.
 *
 * 2. The iframe has no access to the host's `localStorage`, cookies, or
 *    IndexedDB. State MUST be re-hydrated via the `init` message after
 *    the iframe sends `ready`. The protocol carries the dashboard
 *    classification, bindings, and theme inside `init`.
 *
 * 3. An attacker page that embeds the same iframe URL in their own
 *    document cannot read postMessage events the host iframe sends to
 *    its parent (browser scopes postMessage to the same Window pair),
 *    but they CAN deliver forged messages to ANY frame. The host's
 *    Zod-validated parse step is therefore part of defense in depth —
 *    if a forged payload reaches us we reject it at the schema layer
 *    even though sender identity already failed.
 *
 * Every message is validated against a Zod discriminated union at BOTH
 * ends:
 *   - Host before relaying / acting (server-validated when proxying to
 *     /api/v1/argus-live/data-fetch).
 *   - Iframe before treating an `init` / `binding.result` payload as
 *     authoritative (defense against a parent that has been compromised
 *     and is feeding nonsense data; the iframe still sandboxes against
 *     "what type can I trust").
 *
 * Payload caps are enforced by the host (counts byte length of the
 * serialized message) — §4.4 lists 200 KB / 60s and ≤30 fetches / 60s.
 * Caps are enforced in postmessage-host.ts (AL-B1); this file only
 * defines shapes.
 */

import { z } from 'zod';
import { ExportControlSchema, SensitivitySchema } from '@argusai/shared/schemas/document.schema.js';

// ─── Common sub-schemas ─────────────────────────────────────────────────

/**
 * Classification tag carried with every binding result. Mirrors the
 * platform classification shape from CLAUDE.md §11.3.
 *
 * The two alphabets are the shared ones (document.schema.ts, LB3-T1): the
 * runtime bundle is built by esbuild with `bundle: true`, so the shared
 * module (zod-only, no node imports) is inlined into the iframe IIFE — the
 * iframe and the host validate against the same declaration. Import the
 * schema module by its subpath, never `@argusai/shared`'s index (which
 * pulls node-only modules into a browser bundle).
 */
export const ClassificationSchema = z
  .object({
    sensitivity: SensitivitySchema,
    exportControl: ExportControlSchema,
  })
  .strict();
export type Classification = z.infer<typeof ClassificationSchema>;

/**
 * UUID regex — permissive (accepts the v1-v5 variants AND the all-zero
 * nil UUID). We use a regex rather than `uuidString()` because Zod
 * v4's `.uuid()` enforces the RFC 4122 variant nibble strictly, which
 * would reject test IDs like `00000000-0000-0000-0000-000000000001`.
 * Server-generated IDs are always RFC-compliant; the regex defends
 * against malformed payloads, not against UUID-version mismatch.
 */
const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const uuidString = () =>
  z.string().regex(UUID_PATTERN, 'must be a UUID');
const bindingIdString = () => z.string().min(1).max(120);

/**
 * A binding descriptor sent to the iframe at init. Binding IDs mirror
 * dashboard manifests (`pipeline-summary`, `crm_deals`, etc.), not UUIDs.
 * The iframe references bindings by `id` when calling `binding.fetch`; the
 * host resolves the binding to its tool + params at fetch time. The iframe
 * never sees the full MCP tool name or its raw params after init.
 */
export const BindingDescriptorSchema = z
  .object({
    id: bindingIdString(),
    name: z.string().min(1).max(200),
    /**
     * Hint at the classification level the host expects this binding
     * to return. Display-only; the binding result carries the actual
     * classification, which may be HIGHER (more restrictive) but never
     * lower than the hint. Iframe must NEVER trust the hint over the
     * actual `binding.result.classification`.
     */
    classificationHint: ClassificationSchema,
    refreshIntervalMs: z.number().int().min(5000).max(86_400_000).nullable(),
  })
  .strict();
export type BindingDescriptor = z.infer<typeof BindingDescriptorSchema>;

export const ThemeSchema = z
  .object({
    mode: z.enum(['light', 'dark']),
    primaryColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, 'must be #RRGGBB')
      .default('#1890ff'),
    surfaceColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .default('#ffffff'),
  })
  .strict();
export type Theme = z.infer<typeof ThemeSchema>;

// ─── Protocol — runtime version ──────────────────────────────────────────

/**
 * Runtime protocol version. Bump when the discriminated union changes
 * shape. The iframe sends this in the `ready` message and the host
 * verifies compatibility; mismatched majors result in `freeze` +
 * surfaced error to the user (no implicit fallback).
 *
 * 1.1.0 (Cockpit Phase 3, §2.C) — adds the additive `EXECUTE_ACTION`
 * (iframe→host) + `ACTION_RESULT` (host→iframe) message pair for gated
 * write/execute from a dashboard. Minor bump: every 1.0.x message shape
 * is unchanged, so a 1.0 host/iframe still interoperates for the read path.
 *
 * 1.2.0 (Cockpit Phase 4, §2.A — STATE pillar) — adds the additive
 * `READ_STATE` / `WRITE_STATE` (iframe→host) + `STATE_RESPONSE`
 * (host→iframe) messages for the Tables-backed own-state store + per-user
 * view state. Minor bump: every 1.1.x message shape is unchanged, so a 1.1
 * host/iframe still interoperates for the read + action paths.
 */
export const RUNTIME_PROTOCOL_VERSION = '1.2.0' as const;

// ─── Host → Iframe events ────────────────────────────────────────────────

export const HostToIframeEventSchema = z.discriminatedUnion('type', [
  /** Sent once after iframe emits `ready`. Carries the runtime's
   *  state-of-the-world: classification, bindings, theme. */
  z
    .object({
      type: z.literal('READY'),
      protocolVersion: z.string(),
      bindings: z.array(BindingDescriptorSchema),
      classification: ClassificationSchema,
      theme: ThemeSchema,
    })
    .strict(),

  /** Response to a FETCH_DATA request. */
  z
    .object({
      type: z.literal('DATA_RESPONSE'),
      requestId: uuidString(),
      bindingId: bindingIdString(),
      /** Opaque data payload — host has already classification-checked,
       *  PII-redacted, and HTML-escaped string fields per §4.5 row 1. */
      data: z.unknown(),
      classification: ClassificationSchema,
      /** Host-side timestamp the data was assembled, for `dataAsOf`
       *  display in the dashboard. */
      dataAsOf: z.string().datetime(),
    })
    .strict(),

  /** Host-side error (auth denied / rate-limited / tool unavailable). */
  z
    .object({
      type: z.literal('ERROR'),
      requestId: uuidString().optional(),
      bindingId: bindingIdString().optional(),
      code: z.enum([
        'CLASSIFICATION_DENIED',
        'PERMISSION_DENIED',
        'RATE_LIMITED',
        'TOOL_UNAVAILABLE',
        'INVALID_REQUEST',
        'INTERNAL_ERROR',
      ]),
      message: z.string().max(2000),
      retryAfterMs: z.number().int().nonnegative().optional(),
    })
    .strict(),

  /**
   * Cockpit Phase 3 (§2.C) — response to an EXECUTE_ACTION request. The
   * host's execute-action endpoint mints the ExecutionAuthorization (the
   * iframe NEVER mints authz); this carries the outcome back into the
   * sandbox. `status`:
   *   - 'executed'         the write ran (auto_execute); `result` is the
   *                        sanitized tool/workflow outcome.
   *   - 'pending_approval' an approval row was created (approval_required);
   *                        `approvalId` lets the dashboard surface the card.
   *   - 'forbidden'        read_only / no access / gating denies it.
   *   - 'error'            the dispatch failed (gateway/workflow error).
   */
  z
    .object({
      type: z.literal('ACTION_RESULT'),
      requestId: uuidString(),
      actionId: bindingIdString(),
      status: z.enum(['executed', 'pending_approval', 'forbidden', 'error']),
      /** Set when status='pending_approval'. */
      approvalId: uuidString().optional(),
      /** Set when status='executed' — the sanitized result payload. */
      result: z.unknown().optional(),
      /** Stable code for forbidden/error so the dashboard can branch. */
      code: z.string().max(80).optional(),
      /** Human-readable message for forbidden/error. */
      message: z.string().max(2000).optional(),
    })
    .strict(),

  /**
   * Cockpit Phase 4 (§2.A) — response to a READS_STATE / WRITE_STATE request.
   * The host's state endpoint reads/writes the Tables-backed own-state store
   * (durable rows) + the per-user view state. The iframe NEVER writes the DB
   * directly; the host sets organization_id from the server authContext and
   * classification-gates every row. `op` echoes which request this answers
   * (read_rows / write_row / read_user_state / write_user_state) so the
   * iframe can route by the round-trip requestId AND assert the shape.
   * `status`:
   *   - 'ok'        the operation succeeded; `data` carries the payload
   *                 (rows + nextCursor for reads; { rowId, rowKey } for a row
   *                 write; { state } for user-state ops).
   *   - 'forbidden' classification / no access / clearance unverified.
   *   - 'error'     the state op failed (table not found / DB error).
   */
  z
    .object({
      type: z.literal('STATE_RESPONSE'),
      requestId: uuidString(),
      op: z.enum([
        'read_rows',
        'write_row',
        'read_user_state',
        'write_user_state',
      ]),
      status: z.enum(['ok', 'forbidden', 'error']),
      /** Set when status='ok' — the op-specific sanitized payload. */
      data: z.unknown().optional(),
      /** Stable code for forbidden/error so the dashboard can branch. */
      code: z.string().max(80).optional(),
      /** Human-readable message for forbidden/error. */
      message: z.string().max(2000).optional(),
    })
    .strict(),
]);
export type HostToIframeEvent = z.infer<typeof HostToIframeEventSchema>;

// ─── Iframe → Host events ────────────────────────────────────────────────

export const IframeToHostEventSchema = z.discriminatedUnion('type', [
  /** First message the iframe sends after the runtime has booted.
   *  Carries the protocol version so the host can verify compatibility. */
  z
    .object({
      type: z.literal('READY'),
      protocolVersion: z.string(),
    })
    .strict(),

  /** Iframe requests data for a known binding. Host re-checks
   *  permissions + classification, dispatches through the MCP gateway,
   *  responds with DATA_RESPONSE or ERROR. */
  z
    .object({
      type: z.literal('FETCH_DATA'),
      requestId: uuidString(),
      bindingId: bindingIdString(),
      /** Caller-supplied params merged with binding-stored defaults.
       *  Host validates against the binding's param shape before
       *  dispatching. */
      params: z.record(z.string(), z.unknown()).optional(),
    })
    .strict(),

  /** Iframe announces its rendered height so the host can resize the
   *  outer container. Bounded to avoid a runaway iframe forcing the
   *  page to scroll. */
  z
    .object({
      type: z.literal('RESIZE'),
      heightPx: z.number().int().min(0).max(20000),
    })
    .strict(),

  /**
   * Cockpit Phase 3 (§2.C) — iframe asks the host to EXECUTE a declared
   * action binding. The iframe references the action by `actionId` only; the
   * host resolves the action's tool/workflow + gating from the published
   * version, mints the ExecutionAuthorization, and dispatches (or creates an
   * approval). The iframe NEVER mints authz. `inputOverride` is merged onto
   * the action's saved params subject to the same allow-list discipline the
   * read path uses; host validates server-side before dispatch.
   */
  z
    .object({
      type: z.literal('EXECUTE_ACTION'),
      requestId: uuidString(),
      actionId: bindingIdString(),
      inputOverride: z.record(z.string(), z.unknown()).optional(),
    })
    .strict(),

  /**
   * Cockpit Phase 4 (§2.A) — iframe asks the host to READ from the
   * Tables-backed own-state store (durable rows) OR the caller's per-user
   * view state. `target`:
   *   - 'rows'        read durable rows from the cockpit's state Table
   *                   (classification-gated; rows above clearance excluded).
   *   - 'user_state'  read THIS user's view state (active tab, filters, read
   *                   markers, dismissed chips). Never another user's.
   * `tableSlug` names the cockpit's own state table. `query` (rows only)
   * carries an optional jsonb-containment `filter`, `limit`, `cursor`.
   * `rowKey` (user_state only) scopes the state to a specific row; omit for
   * table-level state.
   */
  z
    .object({
      type: z.literal('READ_STATE'),
      requestId: uuidString(),
      target: z.enum(['rows', 'user_state']),
      tableSlug: z.string().min(1).max(120),
      query: z
        .object({
          filter: z.record(z.string(), z.unknown()).optional(),
          limit: z.number().int().min(1).max(1000).optional(),
          cursor: z.string().max(64).optional(),
        })
        .strict()
        .optional(),
      rowKey: z.string().min(1).max(255).optional(),
    })
    .strict(),

  /**
   * Cockpit Phase 4 (§2.A) — iframe asks the host to WRITE to the
   * Tables-backed own-state store OR the caller's per-user view state. The
   * iframe NEVER writes the DB directly: the host sets organization_id from
   * the server authContext (never the client) and classification-gates the
   * write. `target`:
   *   - 'row'         upsert a durable row by (tableSlug, rowKey). `data` is
   *                   the row payload; `classification` is the row's tag (≤
   *                   table ceiling + actor clearance, enforced server-side).
   *   - 'user_state'  shallow-merge `data` into THIS user's view state. Never
   *                   touches another user's state. `rowKey` (optional) scopes
   *                   it to a row; omit for table-level state.
   */
  z
    .object({
      type: z.literal('WRITE_STATE'),
      requestId: uuidString(),
      target: z.enum(['row', 'user_state']),
      tableSlug: z.string().min(1).max(120),
      /** Required for target='row'; the durable row's stable business key. */
      rowKey: z.string().min(1).max(255).optional(),
      /** The write payload (row data for 'row'; the state patch for
       *  'user_state'). */
      data: z.record(z.string(), z.unknown()),
      /** Optional row classification tag (target='row' only). Server clamps
       *  to ≤ table ceiling + actor clearance; an over-classified write is
       *  rejected, never silently downgraded. */
      classification: ClassificationSchema.optional(),
    })
    .strict(),

  /** Iframe emits an audit event the host should log under
   *  `dashboard.runtime.*`. Strictly-shaped to prevent the iframe from
   *  forging arbitrary audit categories. */
  z
    .object({
      type: z.literal('AUDIT_EVENT'),
      /** Subset of dashboard.runtime.* event types per §3.4. */
      eventType: z.enum([
        'dashboard.runtime.crashed',
        'dashboard.runtime.throttled',
        'dashboard.runtime.binding.requested',
      ]),
      /** Up to 4 KB of structured metadata; host strips PII before
       *  persisting to activity_events. */
      metadata: z.record(z.string(), z.unknown()).optional(),
    })
    .strict(),

  /** Iframe reports an uncaught runtime error. The host records
   *  `dashboard.runtime.crashed` and may surface a "this dashboard
   *  failed to render" card. Distinct from AUDIT_EVENT because the
   *  message body can be large and is treated as best-effort
   *  diagnostic, not authoritative audit content. */
  z
    .object({
      type: z.literal('ERROR'),
      message: z.string().max(4000),
      stack: z.string().max(8000).optional(),
    })
    .strict(),
]);
export type IframeToHostEvent = z.infer<typeof IframeToHostEventSchema>;

// ─── Validation helpers ──────────────────────────────────────────────────

export type HostToIframeEventType = HostToIframeEvent['type'];
export type IframeToHostEventType = IframeToHostEvent['type'];

/**
 * The canonical list of event types each direction accepts. Useful for
 * test assertions and for the runtime-bundle's switch dispatch.
 */
export const HOST_TO_IFRAME_EVENT_TYPES: readonly HostToIframeEventType[] = [
  'READY',
  'DATA_RESPONSE',
  'ERROR',
  // Cockpit Phase 3 (§2.C).
  'ACTION_RESULT',
  // Cockpit Phase 4 (§2.A).
  'STATE_RESPONSE',
] as const;

export const IFRAME_TO_HOST_EVENT_TYPES: readonly IframeToHostEventType[] = [
  'READY',
  'FETCH_DATA',
  'RESIZE',
  'AUDIT_EVENT',
  'ERROR',
  // Cockpit Phase 3 (§2.C).
  'EXECUTE_ACTION',
  // Cockpit Phase 4 (§2.A).
  'READ_STATE',
  'WRITE_STATE',
] as const;

/**
 * Parse an inbound `MessageEvent.data` against the host-direction
 * schema. Returns the typed event on success, null on failure (which
 * caller MUST treat as "drop the message and audit-log a
 * sandbox.violation"). Never throws — postMessage handlers cannot
 * propagate exceptions usefully.
 *
 * Defense in depth: the discriminated union ensures the `type` field
 * is one of the known constants. Unknown types fail validation rather
 * than falling through.
 */
export function parseIframeToHostEvent(data: unknown): IframeToHostEvent | null {
  const result = IframeToHostEventSchema.safeParse(data);
  return result.success ? result.data : null;
}

/**
 * Parse an inbound host-direction event inside the iframe runtime.
 * Same null-on-failure contract.
 */
export function parseHostToIframeEvent(data: unknown): HostToIframeEvent | null {
  const result = HostToIframeEventSchema.safeParse(data);
  return result.success ? result.data : null;
}
